/**
 * Workspace Assistant Knowledge Base
 * 
 * COMPREHENSIVE GUIDE to every element in the Workspace
 * The assistant uses this to guide users through all features
 */

// ============================================
// WORKSPACE LAYOUT OVERVIEW
// ============================================

export const WORKSPACE_LAYOUT = {
  overview: `The Workspace is divided into THREE main areas:

**1. USER LIST (Left side)** - Width: 340px
   - Shows all your client conversations
   - Search and filter functionality
   - Online/offline status indicators
   - Quick action buttons for each user

**2. MAIN CONTENT (Center)** - Largest area
   - Contains the tab bar at the top
   - Displays content for the active tab
   - Four main tabs: Case Dashboard, Conversation, Application Review, Chatbot Assistant

**3. RIGHT PANEL (Right side)**
   - Changes based on active tab
   - Case Dashboard → Dashboard Assistant (AI help)
   - Conversation → Observer (AI suggestions)
   - Application Review → Workspace Assistant (AI help)
   - Chatbot Assistant → Macros panel`,

  navigation: `To navigate the workspace:
1. Click a user in the User List to load their data
2. Use the tabs at the top to switch between views
3. The right panel automatically shows relevant tools for each tab`
};

// ============================================
// USER LIST - COMPLETE GUIDE
// ============================================

export const USER_LIST_KNOWLEDGE = {
  name: 'User List',
  icon: '👥',
  location: 'Left side of the workspace (340px width)',
  description: 'The User List shows all your client conversations in one organized panel.',
  
  header: {
    title: {
      location: 'Top left of the User List panel',
      description: 'Shows the User List icon and title, plus a Help button (?) for quick assistance.'
    },
    helpButton: {
      location: 'Next to the title',
      icon: '?',
      description: 'Click the question mark icon to see help information about the User List.'
    }
  },

  searchBar: {
    location: 'Below the header',
    icon: '🔍',
    description: 'Type to search for users by name or other details.',
    howToUse: `To search for a user:
1. Click in the search bar
2. Type the user's name or any identifying information
3. The list filters automatically as you type
4. Clear the search by deleting your text or clicking the X`,
    tips: [
      'Search is case-insensitive',
      'Partial matches will show results',
      'You can search by name, email, or case details'
    ]
  },

  filterButton: {
    location: 'Right side of the search bar',
    icon: '⚙️ (filter icon)',
    description: 'Opens filter options to narrow down the user list.',
    options: {
      showOnlineOnly: 'Toggle to show only users who are currently online',
      activityPeriod: {
        all: 'Show all users regardless of activity',
        today: 'Only show users active today',
        week: 'Only show users active this week',
        month: 'Only show users active this month'
      }
    },
    howToUse: `To filter the user list:
1. Click the filter icon next to the search bar
2. A popup appears with filter options
3. Check "Show online only" to see only online users
4. Select an activity period to filter by recent activity
5. Filters apply immediately
6. A badge appears on the filter icon when filters are active`
  },

  userListItem: {
    description: 'Each row in the User List represents one client conversation.',
    elements: {
      avatar: {
        location: 'Left side of each row',
        description: 'User profile picture or initials placeholder'
      },
      onlineStatus: {
        location: 'Small dot overlay on avatar (bottom-right)',
        colors: {
          green: 'User is ONLINE right now - they are active on the website',
          gray: 'User is OFFLINE - not currently active'
        },
        description: 'Shows if the user is currently on your website'
      },
      userName: {
        location: 'Main text in each row',
        description: 'The client\'s name'
      },
      lastMessage: {
        location: 'Below the user name (smaller text)',
        description: 'Preview of the most recent message in the conversation'
      },
      timestamp: {
        location: 'Right side of the row',
        description: 'Shows when the last message was sent (e.g., "2m ago", "Yesterday")'
      },
      unreadBadge: {
        location: 'On the row, typically right side',
        description: 'If present, shows the number of unread messages'
      }
    }
  },

  actionButtons: {
    description: 'Hover over a user row to see action buttons',
    buttons: {
      info: {
        icon: 'ℹ️ (info icon)',
        description: 'View detailed information about the user and their case',
        howToUse: 'Click the info icon to see user details, conversation start date, and other metadata'
      },
      conversationHistory: {
        icon: '💬 (chat icon)',
        description: 'View the full conversation history',
        howToUse: 'Click to see previous conversations with this user'
      },
      allFiles: {
        icon: '📁 (folder icon)',
        description: 'Access all files shared in this conversation',
        howToUse: 'Click to see documents, images, and other files exchanged'
      },
      conclude: {
        icon: '✅ (conclude icon)',
        description: 'Mark the conversation as concluded/completed',
        howToUse: 'Click to send a conclusion message and mark the case as complete. This button has a cooldown period after clicking.'
      },
      remove: {
        icon: '🗑️ (trash icon)',
        description: 'Remove this conversation from your list',
        howToUse: 'Click and confirm to remove the conversation (this does not delete data)'
      }
    }
  },

  howToSelectUser: `To select a user and view their information:
1. Find the user in the list (use search or scroll)
2. Click anywhere on their row
3. Their data loads in all tabs (Dashboard, Conversation, etc.)
4. The selected user row is highlighted
5. You can now switch between tabs to see different views of their case`,

  tips: [
    'The list is sorted by most recent activity by default',
    'Online users are easy to spot by the green status dot',
    'Use filters to find specific types of users quickly',
    'Hover over users to see quick action buttons',
    'The unread badge helps prioritize which conversations need attention'
  ]
};

// ============================================
// TABS - COMPLETE GUIDE
// ============================================

export const WORKSPACE_TABS = {
  // ----------------------------------------
  // TAB 1: CASE DASHBOARD
  // ----------------------------------------
  'caseDashboard': {
    name: 'Case Dashboard',
    icon: '📊',
    tabOrder: 1,
    location: 'First tab in the tab bar',
    description: 'Comprehensive overview of the selected user\'s case with AI-generated insights.',
    purpose: 'Get a complete picture of a client\'s case at a glance - their options, readiness status, and AI-generated summary.',
    
    rightPanel: {
      name: 'Dashboard Assistant',
      icon: '🤖',
      description: 'AI assistant specifically for helping with dashboard-related questions'
    },

    sections: {
      caseInfo: {
        name: 'Case Information',
        location: 'Top section of the dashboard',
        description: 'Basic information about the user\'s case.',
        contains: [
          'User name and contact details',
          'Case type (immigration category)',
          'Case status (Active, Pending, etc.)',
          'Important dates',
          'Assigned expert information'
        ],
        howToUse: 'Review this section first to understand who the client is and their basic situation.'
      },
      
      optionsAnalysis: {
        name: 'Options Analysis',
        location: 'Usually displayed as cards or a list',
        description: 'AI-analyzed options available for this case.',
        contains: [
          'Different visa/immigration pathways',
          'Feasibility assessment for each option',
          'Recommended path highlighted',
          'Pros and cons of each option'
        ],
        howToUse: `Review the options to understand:
1. What pathways are available for this client
2. Which option the AI recommends
3. Why certain options may not be viable`
      },
      
      readinessAnalysis: {
        name: 'Readiness Analysis',
        location: 'Shows case preparation status',
        description: 'Evaluates how prepared the case is for next steps.',
        contains: [
          'Overall readiness score or percentage',
          'Checklist of required items',
          'Status of each requirement (complete/incomplete)',
          'Blockers that need attention'
        ],
        elements: {
          topBlockers: {
            description: 'Shows the most critical items blocking progress',
            howToRead: 'If you see "Top Blockers 3 from 3", it means there are 3 blocking issues and none are resolved yet. Each blocker needs attention before proceeding.'
          }
        },
        howToUse: 'Check this before proceeding to Application Review to know what\'s missing.'
      },
      
      caseSummary: {
        name: 'Case Summary',
        location: 'Usually at the bottom or as a separate card',
        description: 'AI-generated narrative summary of the entire case.',
        contains: [
          'Conversation highlights',
          'Key decisions made',
          'Current status summary',
          'Next steps recommendations'
        ],
        howToUse: 'Read this for a quick catch-up on cases you haven\'t reviewed recently.'
      }
    },

    actions: {
      refresh: {
        description: 'Reload the dashboard with latest data',
        howTo: 'Click the refresh button (usually top-right) to get updated information'
      },
      exportSummary: {
        description: 'Export the case summary',
        howTo: 'Look for an export or download button to save the summary'
      }
    },

    howToUse: `To use the Case Dashboard effectively:
1. Select a user from the User List
2. The dashboard automatically loads their case data
3. Start with Case Information to understand who they are
4. Check Options Analysis to see available pathways
5. Review Readiness Analysis to see what's complete/missing
6. Read the Case Summary for quick context`,

    tips: [
      'Always check the dashboard first when starting work on a case',
      'The readiness analysis tells you if you can proceed to application review',
      'Use the case summary to quickly refresh your memory on returning clients',
      'Pay attention to blockers - they prevent progress until resolved'
    ]
  },

  // ----------------------------------------
  // TAB 2: CONVERSATION
  // ----------------------------------------
  'conversation': {
    name: 'Conversation',
    icon: '💬',
    tabOrder: 2,
    location: 'Second tab in the tab bar',
    description: 'Real-time chat interface for communicating with clients.',
    purpose: 'This is where you have direct conversations with clients. The AI Observer helps by providing suggestions.',

    rightPanel: {
      name: 'Observer',
      icon: '👁️',
      description: 'AI-powered assistant that watches the conversation and provides real-time suggestions, context, and auto-responses.'
    },

    sections: {
      messageArea: {
        name: 'Message Area',
        location: 'Main center section - takes up most of the space',
        description: 'Shows all messages between you and the client.',
        messageTypes: {
          userMessage: {
            appearance: 'Left-aligned, usually in a lighter bubble',
            description: 'Messages sent by the client'
          },
          expertMessage: {
            appearance: 'Right-aligned, usually in a colored bubble',
            description: 'Messages you send to the client'
          },
          systemMessage: {
            appearance: 'Center-aligned or special styling',
            description: 'Automated messages, notifications, or status updates'
          },
          videoCallMessage: {
            appearance: 'Special bubble with video icon',
            description: 'Clickable message to join a video call. Click to open video call in a new window.',
            howTo: 'When you see a video call message, click on it to open the video call page in a new browser tab'
          },
          paymentMessage: {
            appearance: 'Special bubble with payment styling (usually green button)',
            description: 'Payment request sent to the client',
            contains: ['Amount', 'Description', 'Payment link/button']
          },
          whiteboardMessage: {
            appearance: 'Special bubble with whiteboard icon 🎨',
            description: 'Link to join a collaborative whiteboard session',
            howTo: 'Click the whiteboard message to open the whiteboard in a new tab'
          }
        },
        howToRead: 'Messages are shown in chronological order (newest at bottom). Scroll up to see older messages.'
      },

      inputArea: {
        name: 'Message Input Area',
        location: 'Bottom of the conversation panel',
        description: 'Where you type and send messages.',
        components: {
          textInput: {
            location: 'Main text field at the bottom',
            description: 'Type your message here',
            howToUse: 'Click in the field, type your message, then press Enter or click Send'
          },
          sendButton: {
            location: 'Right side of the input area',
            icon: '➤ or Send',
            description: 'Click to send your message',
            howToUse: 'Type your message and click this button (or press Enter)'
          },
          microphoneButton: {
            location: 'In the input area',
            icon: '🎤',
            description: 'Voice recording - speak instead of typing',
            howToUse: `To use voice input:
1. Click the microphone button to start recording
2. Speak your message clearly
3. Click again to stop recording
4. Your speech is converted to text
5. Review and edit if needed, then send`
          },
          attachmentButton: {
            location: 'In the input area',
            icon: '📎',
            description: 'Attach and send files to the client',
            howToUse: `To send a file:
1. Click the attachment/paperclip icon
2. Select a file from your computer
3. The file uploads and is sent in the conversation`
          }
        }
      },

      actionToolbar: {
        name: 'Action Toolbar',
        location: 'Above or near the input area',
        description: 'Quick action buttons for common tasks.',
        buttons: {
          videoCall: {
            icon: '📹',
            text: 'Start Video Call',
            description: 'Initiate a video call with the client',
            howToUse: `To start a video call:
1. Click the "📹 Start Video Call" button
2. A video call link is automatically sent in the chat
3. The client sees a clickable message to join
4. When they click it, the video call opens in a new window
5. You will also be redirected to the video call page
6. Both parties can see and hear each other`,
            result: 'A special video call message appears in the chat that both you and the client can click to join'
          },
          payment: {
            icon: '💳',
            text: 'Request Payment',
            description: 'Send a payment request to the client',
            howToUse: `To request payment:
1. Click the "💳 Request Payment" button
2. A payment dialog opens
3. Enter the amount (in USD)
4. Enter a description of what the payment is for
5. Click Submit/Send
6. A payment message is sent to the client
7. The client can click to make the payment via Rapyd`,
            fields: ['Amount (required)', 'Description (required)'],
            result: 'A styled payment request message appears in the chat with a "Pay Now" button'
          },
          whiteboard: {
            icon: '🎨',
            text: 'Whiteboard',
            description: 'Start a collaborative whiteboard session',
            howToUse: `To start a whiteboard:
1. Click the "🎨 Whiteboard" button
2. A new whiteboard is created automatically
3. A whiteboard link is sent in the chat
4. The whiteboard opens in a new tab for you
5. The client can click the message to join
6. Both can draw, write, and collaborate in real-time`,
            result: 'A whiteboard invitation message appears, and the whiteboard opens in a new browser tab'
          }
        }
      }
    },

    observerPanel: {
      name: 'Observer (Right Panel)',
      location: 'Right side of the screen when on Conversation tab',
      description: 'AI assistant that watches your conversation and provides help.',
      features: {
        contextInfo: {
          description: 'Background information about what the client is asking',
          howToUse: 'Read the context to better understand the client\'s message'
        },
        suggestions: {
          description: 'Recommended actions or topics to address',
          howToUse: 'Review suggestions to see what the AI thinks you should do next'
        },
        autoResponses: {
          description: 'Ready-to-use reply messages',
          appearance: 'Usually shown as blue underlined text',
          howToUse: `To use an auto-response:
1. Look at the suggested responses in the Observer panel
2. Click on any blue underlined text
3. It gets inserted into your message input
4. You can edit it before sending
5. Press Enter or click Send`
        },
        emotionIndicator: {
          description: 'Shows the detected emotional state of the client',
          emotions: {
            positive: 'Client seems happy, satisfied, or cooperative',
            neutral: 'Client is calm and matter-of-fact',
            negative: 'Client may be frustrated, confused, or upset',
            urgent: 'Message needs immediate attention'
          },
          howToUse: 'Check the emotion to adjust your communication style accordingly'
        },
        priorityLevel: {
          description: 'Indicates urgency of Observer messages',
          levels: {
            high: 'Red/urgent - needs immediate attention',
            medium: 'Yellow/moderate - important but not urgent',
            low: 'Gray/normal - informational'
          }
        }
      }
    },

    howToUse: `Complete guide to having a conversation:

1. **Start**: Select a user from the User List
2. **Read**: Review previous messages to understand context
3. **Check Observer**: Look at the right panel for AI suggestions
4. **Respond**: Type your message or use an auto-response
5. **Use Tools**: Start video calls, request payments, or whiteboard as needed
6. **Send**: Press Enter or click Send

**For voice input:**
1. Click the microphone 🎤
2. Speak your message
3. Review the transcribed text
4. Send when ready`,

    tips: [
      'Always check the Observer panel for helpful suggestions',
      'Use auto-responses to save time - you can edit them before sending',
      'Pay attention to emotion indicators to adjust your tone',
      'Start video calls when complex explanations are needed',
      'Use the whiteboard for visual explanations',
      'Request payments directly in chat for convenience'
    ]
  },

  // ----------------------------------------
  // TAB 3: APPLICATION REVIEW
  // ----------------------------------------
  'applicationReview': {
    name: 'Application Review',
    icon: '📋',
    tabOrder: 3,
    location: 'Third tab in the tab bar',
    description: 'Review, validate, and prepare application documents before submission.',
    purpose: 'Ensure all documents are complete, correct, and ready for official submission.',

    rightPanel: {
      name: 'Workspace Assistant',
      icon: '🤖',
      description: 'AI assistant to help with application review questions and document validation'
    },

    sections: {
      documentList: {
        name: 'Document List',
        location: 'Main area showing all uploaded documents',
        description: 'Shows every document the client has uploaded.',
        columns: [
          'Document name/type',
          'Upload date',
          'Status (validated, pending, rejected)',
          'Actions (view, validate, request)'
        ],
        howToRead: 'Each row is one document. Check the status column to see if it\'s approved or needs attention.'
      },
      validationStatus: {
        name: 'Validation Status',
        description: 'Overall validation status of the application.',
        statuses: {
          complete: 'All required documents are uploaded and validated',
          incomplete: 'Some documents are missing or invalid',
          pending: 'Documents uploaded but not yet validated'
        }
      },
      documentPreview: {
        name: 'Document Preview',
        description: 'View the actual document content',
        howToUse: 'Click on a document to preview it, or click a view button'
      }
    },

    actions: {
      validateDocument: {
        description: 'Run validation checks on a document',
        howTo: 'Click the validate button next to any document to check it meets requirements'
      },
      requestDocument: {
        description: 'Ask the client for a missing document',
        howTo: 'Click "Request Document" to send a request to the client'
      },
      previewApplication: {
        description: 'See the complete application package',
        howTo: 'Click "Preview" to see how the final submission will look'
      },
      downloadDocument: {
        description: 'Download a document to your computer',
        howTo: 'Click the download icon next to any document'
      }
    },

    howToUse: `To review an application:

1. **Select user** from User List first
2. **Click Application Review** tab
3. **Review document list** - check all required docs are present
4. **Validate each document** - click validate to run checks
5. **Fix issues** - request missing documents or re-uploads
6. **Preview application** - see the final package
7. **Submit when ready** - all items should be validated`,

    tips: [
      'Check the Case Dashboard readiness analysis first',
      'Validate documents before final submission',
      'Use the Workspace Assistant (right panel) for help',
      'Request missing documents directly from this tab',
      'Preview the complete application before submitting'
    ]
  },

  // ----------------------------------------
  // TAB 4: CHATBOT ASSISTANT
  // ----------------------------------------
  'history': {
    name: 'Chatbot Assistant',
    icon: '🤖',
    tabOrder: 4,
    location: 'Fourth (last) tab in the tab bar',
    description: 'AI-powered chatbot for analyzing cases and quick-access macros.',
    purpose: 'Get AI analysis of cases and use saved text snippets (macros) for quick responses.',

    rightPanel: {
      name: 'Macros',
      icon: '📝',
      description: 'Panel showing your saved text snippets for quick use'
    },

    sections: {
      chatbotInterface: {
        name: 'Chatbot Chat',
        location: 'Main center area',
        description: 'Have AI-powered conversations about the current case.',
        features: [
          'Ask questions about the case',
          'Get AI-generated analysis',
          'Request summaries or recommendations',
          'Natural language interaction'
        ],
        howToUse: `To use the chatbot:
1. Type your question in the input field
2. Press Enter or click Send
3. The AI analyzes the case and responds
4. You can ask follow-up questions
5. The chatbot remembers conversation context`
      }
    },

    macrosPanel: {
      name: 'Macros Panel',
      location: 'Right side of the screen',
      description: 'Saved text snippets you can quickly insert into conversations.',
      features: {
        viewMacros: {
          description: 'See all your saved macros',
          howToUse: 'Scroll through the list to find the macro you need'
        },
        useMacro: {
          description: 'Copy or insert a macro',
          howToUse: 'Click on a macro to copy it, then paste where needed'
        },
        createMacro: {
          description: 'Save a new macro',
          howToUse: `To create a new macro:
1. Click the "+" or "Add" button
2. Enter a name/title for your macro
3. Enter the text content
4. Optionally add a category
5. Save the macro`,
          icon: '+'
        },
        editMacro: {
          description: 'Modify an existing macro',
          howToUse: 'Click the edit icon on any macro to change it'
        },
        deleteMacro: {
          description: 'Remove a macro',
          howToUse: 'Click the delete icon to remove a macro'
        },
        categories: {
          description: 'Organize macros by category',
          howToUse: 'Filter macros by category to find them faster'
        }
      }
    },

    howToUse: `To use the Chatbot Assistant tab:

**Using the Chatbot:**
1. Select a user first (so the AI has case context)
2. Type a question like "Summarize this case" or "What are the options?"
3. The AI provides intelligent responses

**Using Macros:**
1. Create macros for text you use frequently
2. When chatting with clients, open this tab
3. Click a macro to copy it
4. Switch to Conversation tab and paste it`,

    tips: [
      'The chatbot has access to the full conversation history',
      'Ask specific questions for better answers',
      'Create macros for greetings, common answers, and closings',
      'Organize macros with categories',
      'Macros save time when responding to common questions'
    ]
  }
};

// ============================================
// RIGHT PANEL CONTENT GUIDE
// ============================================

export const RIGHT_PANEL_GUIDE = {
  overview: 'The right panel changes based on which tab you\'re viewing. Each tab has a specific tool designed to help with that task.',
  
  panels: {
    dashboardAssistant: {
      showsOn: 'Case Dashboard tab',
      name: 'Dashboard Assistant',
      icon: '🤖',
      description: 'AI assistant focused on helping you understand the case dashboard.',
      canHelp: [
        'Explain what each section means',
        'Help interpret the options analysis',
        'Explain readiness status and blockers',
        'Answer questions about the case'
      ]
    },
    observer: {
      showsOn: 'Conversation tab',
      name: 'Observer',
      icon: '👁️',
      description: 'Real-time AI assistant that watches conversations.',
      canHelp: [
        'Provide context about messages',
        'Suggest responses',
        'Detect client emotions',
        'Offer auto-response messages'
      ]
    },
    workspaceAssistant: {
      showsOn: 'Application Review tab',
      name: 'Workspace Assistant',
      icon: '🤖',
      description: 'AI assistant focused on helping with application review.',
      canHelp: [
        'Answer questions about documents',
        'Explain validation requirements',
        'Guide through the review process',
        'Help troubleshoot issues'
      ]
    },
    macros: {
      showsOn: 'Chatbot Assistant tab',
      name: 'Macros',
      icon: '📝',
      description: 'Your saved text snippets for quick use.',
      features: [
        'View all saved macros',
        'Create new macros',
        'Edit existing macros',
        'Organize by category'
      ]
    }
  }
};

// ============================================
// STEP-BY-STEP WORKFLOWS
// ============================================

export const WORKFLOWS = {
  startVideoCall: {
    title: 'How to Start a Video Call',
    steps: [
      '1. Select a user from the User List',
      '2. Go to the Conversation tab',
      '3. Find the toolbar above the message input',
      '4. Click the "📹 Start Video Call" button',
      '5. A video call link is automatically sent in the chat',
      '6. The video call opens in a new browser tab for you',
      '7. When the client clicks the message, they join the call',
      '8. Both parties can see and hear each other'
    ],
    tips: [
      'Make sure your camera and microphone are allowed',
      'The client will see a clickable message in their chat',
      'Video calls open in a new window/tab'
    ]
  },

  requestPayment: {
    title: 'How to Request Payment',
    steps: [
      '1. Select a user from the User List',
      '2. Go to the Conversation tab',
      '3. Find the toolbar above the message input',
      '4. Click the "💳 Request Payment" button',
      '5. A payment dialog opens',
      '6. Enter the Amount (e.g., 500)',
      '7. Enter a Description (e.g., "Consultation fee")',
      '8. Click Submit',
      '9. A styled payment request appears in the chat',
      '10. The client can click to pay via secure payment (Rapyd)'
    ],
    tips: [
      'Always include a clear description',
      'Double-check the amount before sending',
      'The payment is processed securely through Rapyd'
    ]
  },

  startWhiteboard: {
    title: 'How to Start a Whiteboard Session',
    steps: [
      '1. Select a user from the User List',
      '2. Go to the Conversation tab',
      '3. Find the toolbar above the message input',
      '4. Click the "🎨 Whiteboard" button',
      '5. A whiteboard is created and a link is sent in chat',
      '6. The whiteboard opens in a new tab for you',
      '7. The client clicks the message to join',
      '8. Both of you can draw and collaborate in real-time'
    ],
    tips: [
      'Great for visual explanations',
      'Both parties can draw simultaneously',
      'The whiteboard content persists'
    ]
  },

  useVoiceInput: {
    title: 'How to Use Voice Input (Microphone)',
    steps: [
      '1. Go to the Conversation tab',
      '2. Find the microphone icon 🎤 in the input area',
      '3. Click the microphone to start recording',
      '4. Speak your message clearly',
      '5. Click the microphone again to stop recording',
      '6. Your speech is converted to text',
      '7. Review the text in the input field',
      '8. Edit if needed, then press Send'
    ],
    tips: [
      'Speak clearly for better transcription',
      'You can edit the text before sending',
      'Allow microphone permissions when prompted'
    ]
  },

  useObserver: {
    title: 'How to Use the Observer Auto-Responses',
    steps: [
      '1. Go to the Conversation tab',
      '2. Look at the right panel (Observer)',
      '3. The Observer shows suggestions based on the conversation',
      '4. Look for blue underlined text (auto-responses)',
      '5. Click any auto-response to insert it into your message',
      '6. Edit the text if you want to customize it',
      '7. Press Enter or click Send'
    ],
    tips: [
      'Auto-responses are AI-generated suggestions',
      'You can and should customize them',
      'Pay attention to the emotion indicator'
    ]
  },

  createMacro: {
    title: 'How to Create a Macro',
    steps: [
      '1. Go to the Chatbot Assistant tab',
      '2. Look at the Macros panel on the right',
      '3. Click the "+" or "Add Macro" button',
      '4. Enter a title for your macro (e.g., "Greeting")',
      '5. Enter the text content',
      '6. Select a category if available',
      '7. Click Save'
    ],
    tips: [
      'Create macros for frequently used text',
      'Use categories to organize them',
      'Good candidates: greetings, common answers, closings'
    ]
  }
};

// ============================================
// COMMON QUESTIONS & ANSWERS
// ============================================

export const COMMON_WORKSPACE_QUESTIONS = {
  // Getting Started
  'how do I start': `Welcome to the Workspace! Here's how to get started:

1. **Select a user** from the User List on the left
2. The **Case Dashboard** loads automatically (first tab)
3. Review their case information, options, and readiness
4. Switch to **Conversation** tab to chat with them
5. The **Observer** on the right provides AI suggestions

Would you like detailed help with any specific feature?`,

  'where do I click': `Here's the workspace layout:

**LEFT SIDE** → User List (click any user to select them)
**CENTER** → Main content area with tabs at the top
**RIGHT SIDE** → Helper panel (changes based on tab)

**TAB BAR (top of center area):**
1. Case Dashboard - case overview
2. Conversation - chat with clients
3. Application Review - review documents
4. Chatbot Assistant - AI help & macros

Click on any tab to switch views!`,

  // User List Questions
  'what is the user list': `The **User List** is on the left side of your workspace.

It shows:
- All your client conversations
- Each user's name and last message preview
- Online/offline status (green dot = online)
- Timestamp of last activity

**To use it:**
- Click any user to load their data
- Use the search bar to find specific users
- Use the filter icon to narrow down the list
- Hover over users to see action buttons`,

  'what do the dots mean': `The dots on user avatars show **online status**:

🟢 **Green dot** = User is ONLINE right now
   - They are currently on your website
   - Good time to engage with them

⚪ **Gray dot** = User is OFFLINE
   - Not currently active
   - They'll see your messages when they return`,

  // Conversation Questions
  'how do I send a message': `To send a message:

1. Select a user from the User List
2. Go to the **Conversation** tab
3. Click in the text input at the bottom
4. Type your message
5. Press **Enter** or click the **Send** button

**Alternative - Voice input:**
- Click the 🎤 microphone icon
- Speak your message
- Click again to stop
- Review and send`,

  'how do I start a video call': `To start a video call:

1. Select a user from the User List
2. Go to the **Conversation** tab
3. Look for the toolbar with action buttons
4. Click **"📹 Start Video Call"**
5. A video link is sent to the chat automatically
6. The video opens in a new tab for you
7. When the client clicks the message, they join

Both of you can then see and hear each other!`,

  'how do I request payment': `To request payment:

1. Select a user from the User List
2. Go to the **Conversation** tab
3. Click **"💳 Request Payment"** in the toolbar
4. Enter the **Amount** (e.g., 150)
5. Enter a **Description** (e.g., "Document review fee")
6. Click Submit

A payment request appears in the chat. The client clicks the button to pay securely.`,

  'how do I start a whiteboard': `To start a whiteboard:

1. Select a user from the User List
2. Go to the **Conversation** tab
3. Click **"🎨 Whiteboard"** in the toolbar
4. A whiteboard link is sent to the chat
5. The whiteboard opens in a new tab for you
6. The client clicks the message to join

Both of you can draw, write, and collaborate in real-time!`,

  // Observer Questions
  'what is the observer': `The **Observer** is your AI-powered conversation assistant.

It appears on the right side when you're in the **Conversation** tab.

**What it provides:**
- **Context** - Background info about messages
- **Suggestions** - Recommended responses or actions
- **Auto-Responses** - Click blue text to insert ready-made replies
- **Emotion Detection** - Shows if client is happy, neutral, or frustrated

**How to use auto-responses:**
1. Look for blue underlined text in the Observer
2. Click it to insert into your message
3. Edit if needed
4. Send`,

  // Dashboard Questions
  'what is the case dashboard': `The **Case Dashboard** is the first tab in the workspace.

It shows a complete overview of a client's case:

**Sections:**
1. **Case Information** - Basic details about the user and case
2. **Options Analysis** - Available pathways (AI-analyzed)
3. **Readiness Analysis** - What's complete vs. missing
4. **Case Summary** - AI-generated overview

**Readiness blockers** show issues that need resolution before proceeding.`,

  'what are top blockers': `**Top Blockers** show the most critical issues preventing progress.

If you see "Top Blockers 3 from 3":
- There are 3 blocking issues
- 0 have been resolved (3 remaining)

Each blocker needs to be addressed:
- Missing documents
- Incomplete information
- Required validations

Check the details of each blocker to understand what needs to be done.`,

  // Macros Questions
  'what are macros': `**Macros** are saved text snippets you can quickly reuse.

They appear in the right panel when on the **Chatbot Assistant** tab.

**How to use:**
1. Click a macro to copy it
2. Paste it in the conversation
3. Edit if needed before sending

**How to create:**
1. Go to Chatbot Assistant tab
2. Click "+" in the Macros panel
3. Add title and text
4. Save

Great for greetings, common answers, and signatures!`,

  // Application Review Questions
  'how do I review an application': `To review an application:

1. Select a user from the User List
2. Click the **Application Review** tab
3. You'll see a list of all uploaded documents
4. **Check each document:**
   - Look at the status (validated, pending, rejected)
   - Click to preview documents
   - Run validation if needed
5. **Request missing documents** if any are incomplete
6. **Preview the full application** before submission

Use the **Workspace Assistant** on the right for help!`,

  // Tab Questions
  'what do the tabs do': `The workspace has 4 main tabs:

1. **📊 Case Dashboard** (Tab 1)
   - Overview of case info, options, readiness
   - Right panel: Dashboard Assistant

2. **💬 Conversation** (Tab 2)
   - Chat with clients
   - Right panel: Observer (AI suggestions)

3. **📋 Application Review** (Tab 3)
   - Review and validate documents
   - Right panel: Workspace Assistant

4. **🤖 Chatbot Assistant** (Tab 4)
   - AI chatbot for case analysis
   - Right panel: Macros`
};

// ============================================
// CONTEXTUAL HELP GENERATOR
// ============================================

export const getWorkspaceContextualHelp = (context) => {
  const { activeTab, selectedUser, rightPanelMode } = context;
  const tabInfo = WORKSPACE_TABS[activeTab];
  
  if (!tabInfo) {
    return {
      greeting: "Hello! I'm your Workspace Assistant. I know everything about this workspace and can help you navigate and use all features. What would you like to know?",
      suggestions: [
        "How do I use the workspace?",
        "What do the tabs do?",
        "How do I start a video call?"
      ]
    };
  }
  
  const suggestions = [];
  
  if (!selectedUser) {
    suggestions.push("How do I select a user?");
    suggestions.push("What does the User List show?");
    suggestions.push("What do the dots mean on user avatars?");
  } else {
    if (activeTab === 'caseDashboard') {
      suggestions.push("What does the Case Dashboard show?");
      suggestions.push("What are Top Blockers?");
      suggestions.push("How do I interpret readiness analysis?");
    } else if (activeTab === 'conversation') {
      suggestions.push("How do I start a video call?");
      suggestions.push("How do I request payment?");
      suggestions.push("What is the Observer?");
    } else if (activeTab === 'applicationReview') {
      suggestions.push("How do I validate documents?");
      suggestions.push("How do I request missing documents?");
      suggestions.push("How do I preview the application?");
    } else if (activeTab === 'history') {
      suggestions.push("How do I use the chatbot?");
      suggestions.push("What are macros?");
      suggestions.push("How do I create a macro?");
    }
  }
  
  return {
    greeting: `You're in **${tabInfo.name}**. ${tabInfo.description} How can I help?`,
    suggestions: suggestions.slice(0, 3)
  };
};

// ============================================
// ASSISTANT MODES
// ============================================

export const ASSISTANT_MODES = {
  'dashboard': {
    name: 'Dashboard Assistant',
    icon: '📊',
    description: 'Helps you understand the Case Dashboard',
    focusAreas: ['case information', 'options analysis', 'readiness', 'blockers', 'summaries']
  },
  'workspace': {
    name: 'Workspace Assistant',
    icon: '📋',
    description: 'Helps you navigate the workspace and review applications',
    focusAreas: ['all tabs', 'document review', 'validation', 'conversation tools', 'workflow']
  }
};

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  WORKSPACE_LAYOUT,
  USER_LIST_KNOWLEDGE,
  WORKSPACE_TABS,
  RIGHT_PANEL_GUIDE,
  WORKFLOWS,
  COMMON_WORKSPACE_QUESTIONS,
  getWorkspaceContextualHelp,
  ASSISTANT_MODES
};
